/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import { Col, Row } from "react-awesome-styled-grid";
import { Else, If, Then, When } from "react-if";
import {
  Box,
  ExtendedTooltip,
  FlexContainer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { GetEventsViewQuery } from "~/generated/graphql";
import { ExpandIcon } from "~/components";
import { TooltipText } from "~/components/Styled/TooltipText";
import { TableWrapper } from "~/components/Tables";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import { dateFormat } from "~/constants";
import { ROUTES } from "~/routing/routes";
import { isNotEmpty } from "~/tools";
import { convertMinutesToTime, RefetchOptions } from "~/views/events/helpers";
import { GridColumns } from "../styled";
import { EntityType, EventEntity } from "../types";
import { ApproveEvent } from "./ApproveEvent/ApproveEvent";
import { eventEntitiesColumns } from "./EventActionsColumns";
import { EventActionSubscriber } from "./EventActionSubscriber";
import { useEventItem } from "./hooks/useEventItem";
import { TooltipIconStatus } from "./TooltipIconStatus";

type Props = {
  event: NonNullable<GetEventsViewQuery["eventsView"]["result"]>[number];
};

export function EventItem({ event }: Props) {
  const {
    eventSource,
    liveEventStatus,
    isEventFinished,
    isEventInProgress,
    isExpanded,
    toggleIsExpanded,
    rowSelection,
    setRowSelection,
    hasEventEntities,
    actionLength,
    eventStatusIconActions,
    approveActions,
    approveBatches,
    sortedEventEntities,
    getData,
    getCountsForAction,
    updateResourceState,
    getActionErrorCodeCountsForEntity,
    getActionDescription,
  } = useEventItem({ event });

  const endTime = eventSource.endTime ?? eventSource.estimatedEndTime;

  const endTimeTZ = dayjs(endTime)
    .tz(event.scheduleTimezone ?? "")
    .format(dateFormat.dayMonthYearHourMinute);

  const startTimeTZ = dayjs(event.startTime ?? "")
    .tz(event.scheduleTimezone ?? "")
    .format(dateFormat.dayMonthYearHourMinute);

  const duration = dayjs(endTime)
    .startOf("minutes")
    .diff(dayjs(event.startTime).startOf("minutes"), "minutes");

  return (
    <Box spacing="spacing02" boxStyle="lightGrey" data-testid="event-row">
      <Row data-testid={event.name}>
        <Col>
          <GridColumns>
            <FlexContainer justifyContent="center" alignItems="center">
              <When condition={hasEventEntities}>
                <ExpandIcon
                  name="chevronDown"
                  title="chevronDown"
                  isExpanded={isExpanded}
                  onClick={toggleIsExpanded}
                />
              </When>
            </FlexContainer>
            <TooltipIconStatus
              status={liveEventStatus}
              actionLength={actionLength}
              type={EntityType.EVENT}
              actions={eventStatusIconActions}
            />
            <NameWithLink
              name={event.name ?? ""}
              link={ROUTES.events.generateDetails(event.id)}
              css={{ paddingLeft: theme.spacing.spacing03 }}
              containerStyle={{ paddingRight: theme.spacing.spacing03 }}
            />
            <FlexContainer alignItems="center">
              <ExtendedTooltip
                caption={
                  <>
                    Plan Time: {startTimeTZ}
                    <br />
                    Timezone: {event.scheduleTimezone}
                  </>
                }
              >
                <TooltipText
                  size="md"
                  tag="span"
                  mr={theme.spacing.spacing01}
                  weight="regular"
                  color={theme.color.text.text01}
                >
                  {dayjs(event.startTime).format(
                    dateFormat.dayMonthYearHourMinute
                  )}
                </TooltipText>
              </ExtendedTooltip>
            </FlexContainer>
            <FlexContainer alignItems="center">
              <ExtendedTooltip
                caption={
                  <>
                    Plan Time: {endTimeTZ}
                    <br />
                    Timezone: {event.scheduleTimezone}
                    <If condition={isEventInProgress}>
                      <Then>
                        <br />
                        End Time will appear when the event is complete
                      </Then>
                    </If>
                  </>
                }
              >
                <TooltipText
                  size="md"
                  tag="span"
                  mr={theme.spacing.spacing01}
                  weight="regular"
                  color={theme.color.text.text01}
                >
                  <If condition={isEventInProgress}>
                    <Then>Awaiting completion</Then>
                    <Else>
                      {dayjs(endTime).format(dateFormat.dayMonthYearHourMinute)}
                    </Else>
                  </If>
                </TooltipText>
              </ExtendedTooltip>
            </FlexContainer>
            <FlexContainer alignItems="center">
              <Text
                size="md"
                tag="span"
                mr={theme.spacing.spacing01}
                weight="regular"
                color={theme.color.text.text01}
              >
                {convertMinutesToTime(duration)}
              </Text>
            </FlexContainer>
            <If
              condition={
                isNotEmpty(approveActions ?? []) ||
                isNotEmpty(approveBatches ?? [])
              }
            >
              <Then>
                <FlexContainer>
                  <ApproveEvent
                    eventId={event.id}
                    hasIcon
                    actions={approveActions}
                    batches={approveBatches}
                    refetchQueries={RefetchOptions.EVENTS}
                  />
                </FlexContainer>
              </Then>
              <Else>
                <div />
              </Else>
            </If>
          </GridColumns>
          <When condition={isExpanded}>
            <TableWrapper<EventEntity>
              loading={false}
              data={sortedEventEntities}
              columns={eventEntitiesColumns({
                refreshEvent: getData,
                getCountsForAction,
                getActionErrorCodeCounts: getActionErrorCodeCountsForEntity,
                getActionDescription,
                isEventFinished,
              })}
              rowSelection={rowSelection}
              selectedRowsHandler={() => null}
              setRowSelection={setRowSelection}
              stripped
            />
          </When>
          <EventActionSubscriber
            eventId={event.id}
            isRunning={isEventInProgress}
            onResourceStateUpdate={updateResourceState}
          />
        </Col>
      </Row>
    </Box>
  );
}
